/*
 * Copyright (c) 2022 NEXTWELL INTL SRL. All Rights Reserved.
 * This software is the proprietary information of NEXTWELL INTL SRL.
 * Use is subject to license and non-disclosure terms
 */

import {
  crmRouterSegment,
  customerProfileRouterSegment,
  customerRouterSegment,
  newCustomerRouterSegment
} from './crm-navigation-constants';

export const customerSmallProfileUrl = (customerUuid: any) => ['/', {outlets: {popup: ['popup', crmRouterSegment, 's', customerRouterSegment, customerProfileRouterSegment, customerUuid]}}]
export const addCustomerUrl = [
  '/',
  {
    outlets: {
      'popup-full': ['popup-full', crmRouterSegment, 's', customerRouterSegment, newCustomerRouterSegment],
    },
  }
]

export type CustomerAddFromExtractionAddress = {
  addressLine?: string | null;
  streetName?: string | null;
  streetNumber?: string | null;
  city?: string | null;
  country?: string | null;
  postalCode?: string | null;
  state?: string | null;
  lat?: number | null;
  lng?: number | null;
};

export type CustomerContactFromExtractionPrefill = {
  name?: string | null;
  email?: string | null;
  phoneNumber?: string | null;
};

export type CustomerAddFromExtractionPrefill = {
  companyName?: string | null;
  uniqueIdNumber?: string | null;
  country?: string | null;
  mainAddress?: string | null;
  address?: CustomerAddFromExtractionAddress | null;
  contact?: CustomerContactFromExtractionPrefill | null;
};

const writeContactMatrixParams = (
  matrixParams: Record<string, string>,
  contact: CustomerContactFromExtractionPrefill | null | undefined,
) => {
  if (!contact) return;
  if (contact.name) matrixParams['contactName'] = contact.name;
  if (contact.email) matrixParams['contactEmail'] = contact.email;
  if (contact.phoneNumber) matrixParams['contactPhone'] = contact.phoneNumber;
};

const readContactMatrixParams = (
  params: Record<string, string | undefined>,
): CustomerContactFromExtractionPrefill | null => {
  const name = params['contactName'] || null;
  const email = params['contactEmail'] || null;
  const phoneNumber = params['contactPhone'] || null;
  if (!name && !email && !phoneNumber) return null;
  return { name, email, phoneNumber };
};

export const addCustomerFromExtractionUrl = (prefill: CustomerAddFromExtractionPrefill = {}) => {
  const matrixParams: Record<string, string> = {};
  if (prefill.companyName) matrixParams['companyName'] = prefill.companyName;
  if (prefill.uniqueIdNumber) matrixParams['uniqueIdNumber'] = prefill.uniqueIdNumber;
  if (prefill.country) matrixParams['country'] = prefill.country;
  if (prefill.mainAddress) matrixParams['mainAddress'] = prefill.mainAddress;

  const a = prefill.address ?? null;
  if (a?.addressLine) matrixParams['addressLine'] = a.addressLine;
  if (a?.streetName) matrixParams['streetName'] = a.streetName;
  if (a?.streetNumber) matrixParams['streetNumber'] = a.streetNumber;
  if (a?.city) matrixParams['city'] = a.city;
  if (a?.country) matrixParams['addressCountry'] = a.country;
  if (a?.postalCode) matrixParams['postalCode'] = a.postalCode;
  if (a?.state) matrixParams['state'] = a.state;
  if (a?.lat != null) matrixParams['lat'] = String(a.lat);
  if (a?.lng != null) matrixParams['lng'] = String(a.lng);

  writeContactMatrixParams(matrixParams, prefill.contact);

  const lastSegment: (string | Record<string, string>)[] = Object.keys(matrixParams).length
    ? [newCustomerRouterSegment, matrixParams]
    : [newCustomerRouterSegment];

  return [
    '/',
    {
      outlets: {
        'popup-full': [
          'popup-full',
          crmRouterSegment,
          's',
          customerRouterSegment,
          ...lastSegment,
        ],
      },
    },
  ];
};

export const addCustomerContactFromExtractionUrl = (
  customerUuid: string,
  prefill: CustomerContactFromExtractionPrefill = {},
) => {
  const matrixParams: Record<string, string> = {};
  writeContactMatrixParams(matrixParams, prefill);

  const newSegment: (string | Record<string, string>)[] = Object.keys(matrixParams).length
    ? ['new', matrixParams]
    : ['new'];

  return [
    '/',
    {
      outlets: {
        'popup-full': [
          'popup-full',
          crmRouterSegment,
          's',
          customerRouterSegment,
          'contacts',
          customerUuid,
          ...newSegment,
        ],
      },
    },
  ];
};

export const readCustomerContactFromExtractionPrefill = (
  params: Record<string, string | undefined>,
): CustomerContactFromExtractionPrefill | null => readContactMatrixParams(params);

export const readCustomerAddFromExtractionPrefill = (
  params: Record<string, string | undefined>,
): CustomerAddFromExtractionPrefill | null => {
  const str = (key: string): string | null => {
    const v = params[key];
    return v != null && v !== '' ? v : null;
  };
  const num = (key: string): number | null => {
    const v = params[key];
    if (v == null || v === '') return null;
    const n = Number(v);
    return Number.isNaN(n) ? null : n;
  };

  const address: CustomerAddFromExtractionAddress = {
    addressLine: str('addressLine'),
    streetName: str('streetName'),
    streetNumber: str('streetNumber'),
    city: str('city'),
    country: str('addressCountry'),
    postalCode: str('postalCode'),
    state: str('state'),
    lat: num('lat'),
    lng: num('lng'),
  };
  const hasAddress = Object.values(address).some((v) => v !== null);

  const contact = readContactMatrixParams(params);

  const prefill: CustomerAddFromExtractionPrefill = {
    companyName: str('companyName'),
    uniqueIdNumber: str('uniqueIdNumber'),
    country: str('country'),
    mainAddress: str('mainAddress'),
    address: hasAddress ? address : null,
    contact,
  };
  const hasAny = Object.values(prefill).some((v) => v !== null);
  return hasAny ? prefill : null;
};
